import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import Config from '@/Management/GlobalConfiguration';
import DropDownListViewModel from '@/ViewModels/Controls/DropDownListViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import { IWindowViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';

export default class WindowViewModel extends ControlViewModel {

    /**
     * Das jQuery-Element welches den Window-Content beinhaltet
     */
    public content: KnockoutObservable<JQuery>;

    /**
     * Der Titel des Fensters
     */
    public headline: LabelViewModel;

    /**
     * Eine DropDownList im Header
     */
    public dropDownList: DropDownListViewModel;

    /**
     * Gibt an, ob das Window verschiebbar ist
      */
    public isDraggable: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Window minimiert ist
     */
    public isMinimized: KnockoutObservable<boolean>;

    /**
     * Löst das minimize-Event aus
     */
    public triggerMinimize: KnockoutObservable<boolean>;

    /**
     * Löst das restore-Event aus
     */
    public triggerRestore: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Window maximiert ist
     */
    public isMaximized: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Fenster modal ist
     */
    public isModal: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Fenster eine MessageBox ist
     */
    public isMessageBox: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Größe des Fensters verändert werden kann
     */
    public isResizable: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Fenster initial geöffnet ist
     */
    public isVisible: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Fenster geöffnet ist
     */
    public isOpen: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Fenster minimiert werden kann
     */
    public allowMinimize: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Fenster manuell geschlossen werden kann
     */
    public allowClose: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob es sich um ein Login-Window handelt
     */
    public isLoginWindow: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob es sich um ein SearchListView-Window handelt
     */
    public isSearchListViewWindow: KnockoutObservable<boolean>;

    /**
     * Definiert die Breite des Fensters
     */
    public width: KnockoutObservable<number | string>;

    /**
     * Definiert die Höhe des Fensters
     */
    public height: KnockoutObservable<number | string>;

    /**
     * Definiert die maximale Breite des Fensters
     */
    public maxWidth: KnockoutObservable<number | string>;

    /**
     * Definiert die maximale Höhe des Fensters
     */
    public maxHeight: KnockoutObservable<number | string>;

    /**
     * Handler für das Öffnen des Fensters
     */
    public onOpen: Function;

    /**
     * Handler für das "Aktivieren" (abgeschlossene Öffnen) des Fensters
     */
    public onActivate: Function;

    /**
     * Handler für das Schließen des Fensters
     */
    public onClose: Function;

    /**
     * Bestimmt, ob das Fenster sich beim Ändern der Größe des Browserfensters automatisch neu zentriert.
     * Diese Einstellung sollte nur eine Wirkung haben, wenn isDraggable und isResizable beide false sind.
     */
    public centerOnWindowResize: boolean;

    /**
     * Gibt an, ob das Fenster automatisch fokussiert ist 
     */
    public autoFocus: KnockoutObservable<boolean>;
    
    public controlName: string;

    /**
     * Das ViewModel für das Window
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(protected params: IWindowViewModelConstructorParameter) {
        super(params);
        this.content = this.observableOrDefault(params.content, $());
        this.animation({
            open: {
                effects: 'zoom:in',
                duration: 100
            },
            close: {
                effects: 'zoom:out',
                duration: 100
            }
        });
        this.headline = this.paramOrDefault(params.headline, null);
        this.dropDownList = this.paramOrDefault(params.dropDownList, null);
        this.isDraggable = this.observableOrDefault(params.isDraggable, true);
        this.isMinimized = this.observableOrDefault(params.isMaximized, false);
        this.triggerMinimize = this.observableOrDefault(true);
        this.triggerRestore = this.observableOrDefault(true);
        this.isMaximized = this.observableOrDefault(params.isMaximized, false);
        this.isModal = this.observableOrDefault(params.isModal, false);
        this.isMessageBox = this.observableOrDefault(params.isMessageBox, false);
        this.isResizable = this.observableOrDefault(params.isResizable, false);
        this.isVisible = this.observableOrDefault(params.isVisible, false);
        this.isOpen = this.observableOrDefault(params.isOpen, true).extend({ notify: 'always' });
        this.allowMinimize = this.observableOrDefault(params.allowMinimize, false);
        this.allowClose = this.observableOrDefault(params.allowClose, (() => { 
            if (Config['window'].ShowMessageWindowCrossButton === true) {
                return true;
            }
            else {
                return false;
            }
        })());
        this.isLoginWindow = this.observableOrDefault(params.isLoginWindow, false);
        this.isSearchListViewWindow = this.observableOrDefault(params.isSearchListViewWindow, false);
        this.width = this.observableOrDefault(params.width, null);
        this.height = this.observableOrDefault(params.height, null);
        this.maxWidth = this.observableOrDefault(params.maxWidth, Infinity);
        this.maxHeight = this.observableOrDefault(params.maxHeight, Infinity);
        this.onOpen = this.handlerOrDefault(params.onOpen);
        this.onActivate = this.handlerOrDefault(params.onActivate);
        this.onClose = this.handlerOrDefault(params.onClose);
        this.centerOnWindowResize = this.paramOrDefault(params.centerOnWindowResize, false);
        this.autoFocus = this.observableOrDefault(params.autoFocus, true);
        this.controlName = this.paramOrDefault(params.controlName, 'window');
    }
}

ComponentManager.setViewModel('WindowViewModel', WindowViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');